import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../../Common/isRequestCanceled";
import {
  BASE_URL,
  DELETE_EMPLOYEE,
  EMPLOYEE_LIST,
  EMPLOYEE_LIST_V2,
  EMPLOYEE_DATA,
  PERMISSIONS_LIST,
  UPDATE_PERMISSION,
  RESTORE_EMPLOYEE,
  FOREVER_DELETE_EMPLOYEE,
} from "../../../../Constants/Config";
import { ciUrl } from "../../../../Constants/nodeCi";
import { ToastifyAlert } from "../../../../CommonComponents/ToastifyAlert";

const initialState = {
  loading: false,
  employeelistData: [],
  states: [],
  employeeData: [],
  permissionData: [],
  successMessage: "",
  error: "",
  getEmployeeDataLoading: false,
  storeListData: [],
  employeelistPagination: null,
  employeelistTotalCount: 0,
  employeelistSummary: null,
  loadingMore: false,
};

const mapEmployeeListItem = (employee) => ({
  ...employee,
  full_name:
    employee.full_name ||
    `${employee.f_name ?? ""} ${employee.l_name ?? ""}`.trim(),
});

const extractEmployeeListV2Payload = (payload = {}) => {
  const employees = payload.data ?? payload.result ?? [];
  const pagination = payload.pagination ?? null;
  const totalCount =
    pagination?.total_count ??
    payload.summary?.filtered_count ??
    payload.summary?.total_active_count ??
    employees.length;

  return {
    employees: Array.isArray(employees) ? employees.map(mapEmployeeListItem) : [],
    pagination,
    totalCount,
    summary: payload.summary ?? null,
    storesList: payload.stores_list ?? [],
    states: payload.states,
  };
};

// Generate pening , fulfilled and rejected action type
export const fetchEmployeeListsData = createAsyncThunk(
  "employeelist/fetchEmployeeListsData.",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(ciUrl(EMPLOYEE_LIST), dataNew, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      // throw new Error(error);
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  },
);

export const fetchEmployeeListV2 = createAsyncThunk(
  "employeelist/fetchEmployeeListV2",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(ciUrl(EMPLOYEE_LIST_V2), dataNew, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      const customError = {
        message: error.message,
        status: error.response ? error.response.status : "Network Error",
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  },
);

export const fetchEmployeeData = createAsyncThunk(
  "employeelist/fetchEmployeeData.",
  async (data) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(ciUrl(EMPLOYEE_DATA), dataNew, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response?.data?.status === true) {
        return response?.data;
      } else {
        return {
          result: {},
        };
      }
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  },
);

export const fetchPermissionData = createAsyncThunk(
  "employeelist/fetchPermissionData.",
  async (data) => {
    const { token, ...dataNew } = data;

    try {
      const response = await axios.post(ciUrl(PERMISSIONS_LIST), dataNew, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  },
);

export const deleteEmployee = createAsyncThunk(
  "employeeList/deleteEmployee",
  async (data) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(BASE_URL + DELETE_EMPLOYEE, dataNew, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.status === true) {
        const mydata = {
          employee_id: data.employee_id,
          message: response.data.message,
        };
        return mydata;
      } else if (response.data.status === false) {
        ToastifyAlert(response.data.message, "error");
      }
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  },
);

export const restoreEmployee = createAsyncThunk(
  "employeeList/restoreEmployee",
  async (data) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(BASE_URL + RESTORE_EMPLOYEE, dataNew, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.status === true) {
        return {
          message: response.data.message,
          ...data,
        };
      } else {
        ToastifyAlert(response.data.message, "error");
      }
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Error restoring employee",
      );
    }
  },
);


export const foreverDeleteEmployee = createAsyncThunk(
  "employeeList/foreverDeleteEmployee",
  async (data) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_URL + FOREVER_DELETE_EMPLOYEE,
        dataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.data.status === true) {
        return {
          message: response.data.message,
          ...data,
        };
      } else {
        ToastifyAlert(response.data.message, "error");
      }
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Error permanently deleting employee",
      );
    }
  },
);

const AddEmployeeSlice = createSlice({
  name: "employeelist",
  initialState,
  reducers: {
    addToEmployeeList: (state, action) => {
      state.employeelistData = [...state.employeelistData, action.payload];
    },
    editEmployee: (state, action) => {
      state.employeelistData = state.employeelistData.map((employee) => {
        if (employee.id === action.payload.employee_id) {
          return {
            ...employee, // Spread syntax to copy existing properties
            f_name: action.payload.f_name, // Update the title
            l_name: action.payload.l_name,
            enable_backend_access: action.payload.backendAccess,
            enable_pos_access: action.payload.enablePOSAccess,
            password: action.payload.employeePassword,
            phone: action.payload.phone,
            email: action.payload.email,
            pin: action.payload.pin,
            wages_per_hr: action.payload.wages,
            address: action.payload.address_line_1,
            city: action.payload.city,
            zipcode: action.payload.zip,
            state: action.payload.state,
          };
        } else {
          // This isn't the one we're looking for - leave it as is
          return employee;
        }
      });
    },
    clearEmployeeData: (state) => {
      state.employeeData = [];
    },
    bulkDeleteEmployeesReducer: (state, action) => {
      state.employeelistData = state.employeelistData.filter(
        (item) => !action.payload.includes(item.id),
      );
    },
    clearPermission: (state) => {
      state.permissionData = [];
      state.employeeData = [];
    },
    setEmployeeStates: (state, action) => {
      state.states = action.payload || [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEmployeeListsData.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchEmployeeListsData.fulfilled, (state, action) => {
      state.loading = false;
      state.employeelistData = (action.payload.result || []).filter(
        (item) => item.is_deleted === "0",
      );
      state.storeListData = action.payload?.stores_list || [];
      state.states = action.payload.states;
      state.error = "";
    });
    builder.addCase(fetchEmployeeListsData.rejected, (state, action) => {
      state.loading = false;
      state.employeelistData = [];
      state.error = action.error.message;
    });

    builder.addCase(fetchEmployeeListV2.pending, (state, action) => {
      const pageNumber = action.meta.arg?.page ?? 1;
      if (pageNumber > 1) {
        state.loadingMore = true;
      } else {
        state.loading = true;
      }
    });
    builder.addCase(fetchEmployeeListV2.fulfilled, (state, action) => {
      const pageNumber = action.meta.arg?.page ?? 1;
      const {
        employees,
        pagination,
        totalCount,
        summary,
        storesList,
        states,
      } = extractEmployeeListV2Payload(action.payload);

      const activeEmployees = employees.filter((item) => item.is_deleted === "0");

      state.loading = false;
      state.loadingMore = false;
      if (pageNumber > 1) {
        const existingIds = new Set(state.employeelistData.map((item) => item.id));
        const newEmployees = activeEmployees.filter((item) => !existingIds.has(item.id));
        state.employeelistData = [...state.employeelistData, ...newEmployees];
      } else {
        state.employeelistData = activeEmployees;
      }
      state.employeelistPagination = pagination;
      state.employeelistTotalCount = totalCount;
      state.employeelistSummary = summary;
      if (storesList.length > 0) {
        state.storeListData = storesList;
      }
      if (states) {
        state.states = states;
      }
      state.error = "";
    });
    builder.addCase(fetchEmployeeListV2.rejected, (state, action) => {
      state.loading = false;
      state.loadingMore = false;
      const pageNumber = action.meta.arg?.page ?? 1;
      if (pageNumber === 1) {
        state.employeelistData = [];
        state.employeelistPagination = null;
        state.employeelistTotalCount = 0;
        state.employeelistSummary = null;
      }
      state.error = action.payload?.message || action.error.message;
    });

    builder.addCase(fetchEmployeeData.pending, (state) => {
      state.getEmployeeDataLoading = true;
    });
    builder.addCase(fetchEmployeeData.fulfilled, (state, action) => {
      state.getEmployeeDataLoading = false;
      state.employeeData = action.payload.result;
      state.error = "";
    });
    builder.addCase(fetchEmployeeData.rejected, (state, action) => {
      state.getEmployeeDataLoading = false;
      state.employeeData = {};
      state.error = action.error.message;
    });

    builder.addCase(fetchPermissionData.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchPermissionData.fulfilled, (state, action) => {
      state.loading = false;
      state.permissionData = action.payload.result;
      state.error = "";
    });
    builder.addCase(fetchPermissionData.rejected, (state, action) => {
      state.loading = false;
      state.permissionData = {};
      state.error = action.error.message;
    });

    builder.addCase(deleteEmployee.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteEmployee.fulfilled, (state, action) => {
      state.loading = false;
      state.successMessage = action?.payload?.message;
      state.employeelistData = state?.employeelistData?.filter(
        (item) => item && item.id !== action?.payload?.employee_id,
      );

      state.error = ""; // Reset the error message
    });
    builder.addCase(deleteEmployee.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
  },
});

export const {
  addToEmployeeList,
  editEmployee,
  clearPermission,
  bulkDeleteEmployeesReducer,
  clearEmployeeData,
  setEmployeeStates,
} = AddEmployeeSlice.actions;
export default AddEmployeeSlice.reducer;
